import React, { useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';

import { getL10nMoney } from '@travel/i18n';
import { Tag } from '@travel/ui';

import makeMarkerWithLabel from 'utils/markerWithLabel';

import { ProviderItem } from 'ProviderList-Types';

import styles from './mapPin.module.scss';

export const TRANSPARENT_ICON =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

type Props = {
  /** Instance of map */
  map: google.maps.Map | null;
  /** Object of target provider item */
  item: ProviderItem;
  /** Callback to be called when Marker is created */
  storeMapPinInListAndMap?: (id: string, mapPin: google.maps.Marker) => void;
  /** Callback to be called when Marker is clicked */
  onMapPinClick?: (item: ProviderItem) => void;
  /** String of displayed currency */
  currency: string;
  /** String of displayed language */
  language: string;
  /** Flag to define whether the domestic currency sign will be displayed or not */
  isGlobalCurrency?: boolean;
  /** Number for z-index to render the pin with specific order */
  zIndex?: number;
  /** Reset selected map pin by id */
  resetMapPin?: (id: string) => void;
};

function MapPin(props: Props) {
  const {
    map,
    item,
    storeMapPinInListAndMap,
    onMapPinClick,
    zIndex,
    currency,
    language,
    isGlobalCurrency,
    resetMapPin,
  } = props;
  // Cannot use L10nMoney because the component is being used in GoogleMap
  const priceText = getL10nMoney(language, item?.price?.total || 0, currency, isGlobalCurrency);

  const getLabelContent = useCallback(
    // TODO: Watch out rerendering of this until stable version of map/mapPin component
    (type, color) => {
      const content = item.price ? (
        <Tag className={styles.mapLabel} type={type} color={color}>
          {priceText}~
        </Tag>
      ) : (
        <></>
      );
      // Google API can take only DOM Node, not ReactNode
      const labelWrapper = document.createElement('div');
      labelWrapper.classList.add(styles.labelAnchor);
      labelWrapper.setAttribute('data-testid', `mapPin-wrapper-${item.id}`);

      // preventMapHitsFrom -> this may only happen in SP, since the api doesn't know the action type on SP
      ReactDOM.render(content, labelWrapper);
      return labelWrapper;
    },
    [item.id, item.price, priceText],
  );

  useEffect(() => {
    // reset prev pin when re-render
    resetMapPin?.(item.id);

    const MarkerWithLabel = makeMarkerWithLabel(window.google.maps);
    const position = {
      lat: item.location.lat,
      lng: item.location.lng,
    };
    const labelContent = getLabelContent('filled', 'white');
    const mapPin = new MarkerWithLabel({
      position: position,
      map: map,
      icon: TRANSPARENT_ICON,
      labelContent: labelContent,
      labelClass: styles.label, // CSS class for the label
      zIndex,
    });

    storeMapPinInListAndMap?.(item.id, mapPin);

    mapPin.addListener('click', () => {
      onMapPinClick?.(item);
      map?.setCenter?.(position);
    });
  }, [
    getLabelContent,
    item,
    item.location.lat,
    item.location.lng,
    map,
    onMapPinClick,
    resetMapPin,
    storeMapPinInListAndMap,
    zIndex,
  ]);

  return null;
}

export default MapPin;
